import { useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { QUERY_DEFAULTS } from '@/shared/api/openmetadata';
import type { OpenMetadataHit, OpenMetadataResponse } from '@/shared/api/openmetadata';
import { tagKeys } from '@/shared/lib/query/queryKeys';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';
import type { SearchResult } from '@/entities/search';
import { filterAndMapTags } from '@/entities/search/lib/tagUtils';

import { searchCertificationAssets } from '@/features/tags/api/searchCertificationAssetsApi';

type UseCertificationAssetsParams = {
  certificationTagFqn: string;
  filters: FilterState;
  query?: string;
  enabled?: boolean;
  sortBy: SortOption['field'];
  sortOrder: SortOption['direction'];
};

export const useCertificationAssets = ({
  certificationTagFqn,
  filters,
  query = '',
  enabled = true,
  sortBy = 'updatedAt',
  sortOrder = 'desc',
}: UseCertificationAssetsParams) => {
  return useInfiniteQuery({
    queryKey: tagKeys.certificationAssets(certificationTagFqn, filters, query, sortBy, sortOrder),
    queryFn: ({ pageParam = 0 }) =>
      searchCertificationAssets(
        certificationTagFqn,
        filters,
        pageParam as number,
        QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
        query,
        sortBy,
        sortOrder
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage: OpenMetadataResponse, allPages: OpenMetadataResponse[]) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.hits.hits.length, 0);
      const total = lastPage.hits.total.value;
      return totalFetched < total ? totalFetched : undefined;
    },
    select: (data) => {
      if (!data.pages) return { result: [], totalCount: 0 };
      const result = data.pages.flatMap((page) => {
        return page.hits.hits.map((hit: OpenMetadataHit) => ({
          id: hit._id,
          type: hit._source.entityType as SearchResult['type'],
          title: hit._source.displayName ?? hit._source.name,
          description: hit._source.description ?? '',
          path: hit._source.fullyQualifiedName,
          owner: {
            name: hit._source.owners?.[0]?.name ?? '',
            department: '',
            email: '',
          },
          owners: hit._source.owners,
          tags: filterAndMapTags(hit._source.tags),
          tier: hit._source.tier || undefined,
          certification:
            hit._source.certification?.tagLabel?.name && hit._source.certification.tagLabel.tagFQN
              ? {
                  name: hit._source.certification.tagLabel.name,
                  tagFQN: hit._source.certification.tagLabel.tagFQN,
                  description: hit._source.certification.tagLabel.description || undefined,
                  expiryDate: hit._source.certification.expiryDate,
                  appliedDate: hit._source.certification.appliedDate,
                  style: hit._source.certification.tagLabel.style || undefined,
                }
              : undefined,
          followers: [],
          serviceType: hit._source.serviceType,
          updatedAt: dayjs(hit._source.updatedAt).format('YYYY-MM-DD'),
          domains: hit._source.domains,
          _source: hit._source,
        }));
      });
      return { result, totalCount: data.pages[0].hits.total.value ?? 0 };
    },
    enabled: enabled && !!certificationTagFqn,
  });
};
